"use client";
/**
 * The recommended composition: the identity and the figure carry the card.
 *
 * Half this pillar's corpus has no picture. The food endpoint publishes no
 * image field on any row, so three of the six captured rows are permanently
 * picture-less and a media-led default would open on a designed absence for
 * every food a reader searches. What distinguishes one row from another here is
 * the name, whose it is, what its energy figure is measured against, and the
 * macro split — all four of which are type.
 *
 * The vendor's own summary line rides the body where it exists. It is the
 * densest true sentence on this pillar — `Per 101g - Calories: 197kcal | …` —
 * and it is verbatim rather than re-derived, so nothing here can disagree with
 * the arithmetic the vendor published.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardProse,
  CresultcardStack,
  CRC_TILE_INSET,
  CresultcardAlign,
  CresultcardDensity,
  CresultcardFigureScale,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardProseMode,
  CresultcardStackDensity,
  CresultcardTextStep,
  CresultcardTilePadding,
  CresultcardTitleLines,
  useCresultcardLightbox,
} from "@/components/ui/Cresultcard";
import {
  C_NUTRITION_CARD_LEAD_MEDIA,
  C_NUTRITION_CARD_MODIFIER,
} from "./cnutritioncard.constants";
import {
  CnutritioncardEnergy,
  CnutritioncardMacroStrip,
  CnutritioncardMetaLine,
  CnutritioncardShot,
} from "./CnutritioncardMeta";
import { CnutritioncardShell } from "./CnutritioncardShell";
import {
  CnutritioncardVariant,
  type CnutritioncardVariantProps,
} from "./cnutritioncard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Regular];
const HEAD_GAP = 10;
/** The title yields; the energy figure holds its width. */
const CARD_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CnutritioncardLabelLine({
  data,
  servingScope,
  openControl,
}: CnutritioncardVariantProps): ReactElement {
  const { open, viewer } = useCresultcardLightbox({ assets: data.images });

  return (
    <>
      <CnutritioncardShell
        density={CresultcardDensity.Card}
        head={
          <div
            data-el="Cnutritioncard/LabelLine/Header"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: HEAD_GAP,
              minWidth: 0,
              padding: CARD_INSET,
            }}
          >
            <span
              style={{
                display: "block",
                width: C_NUTRITION_CARD_LEAD_MEDIA,
                flex: "0 0 auto",
              }}
            >
              <CnutritioncardShot
                data={data}
                onOpen={() => open(0)}
                scale={CresultcardMediaScale.Thumb}
              />
            </span>
            <CresultcardIdentity
              openControl={openControl}
              secondary={
                <CnutritioncardMetaLine
                  data={data}
                />
              }
              title={data.name}
              titleLines={CresultcardTitleLines.Two}
              truncationOrder={CARD_TRUNCATION}
            />
          </div>
        }
        modifier={C_NUTRITION_CARD_MODIFIER[CnutritioncardVariant.LabelLine]}
        openable
        variant={CnutritioncardVariant.LabelLine}
        body={
          <div
            style={{
              minWidth: 0,
              paddingInline: CARD_INSET,
              paddingBlockEnd: CARD_INSET,
            }}
          >
            <CresultcardStack
              density={CresultcardStackDensity.Regular}
            >
              {/* The energy figure sits in the BODY, not in the identity's
                  trailing slot, and the reason is the scope line under it.
                  The basis can be the vendor's own
                  `1 oz, with bone cooked (yield after bone removed)` — 53
                  characters with its prefix — and a trailing slot beside a
                  title is the one place on a card that cannot absorb that
                  without taking the width from the name. In the body it gets
                  the card's whole inner measure. `Cshoppingcard` puts its
                  scope-bearing price in exactly the same region. */}
              <CnutritioncardEnergy
                align={CresultcardAlign.Start}
                data={data}
                scale={CresultcardFigureScale.Card}
                servingScope={servingScope}
              />
              <CnutritioncardMacroStrip
                data={data}
              />
              {/* Display prose, not a subline. The vendor's summary runs 73
                  to 74 characters on all three captured rows and the blurb
                  runs 35 to 65 — the kit's subline is one line with an
                  ellipsis built in, so TYPICAL content would arrive cut. */}
              {data.summary != null && (
                <CresultcardProse
                  displayStep={CresultcardTextStep.Meta}
                  mode={CresultcardProseMode.Display}
                  text={data.summary}
                />
              )}
              {data.description != null && (
                <CresultcardProse
                  displayStep={CresultcardTextStep.Body}
                  mode={CresultcardProseMode.Display}
                  text={data.description}
                />
              )}
            </CresultcardStack>
          </div>
        }
      />
      {viewer}
    </>
  );
}
